import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useTimezone } from "src/business-components/input/TimeZoneSelector/TimeZoneSelector.hooks";
import { DateTimeIcon } from "src/components/general/Icons/Icons";
import SelectPopover from "src/components/input/SelectPopover/SelectPopover";
import { timeWindowNoDataPopoverToggleAtom } from "src/store/atoms/ui.atoms";
import TimeWindowContent from "./components/TimeWindowContent/TimeWindowContent";
import TimeWindowNoDataPopover from "./components/TimeWindowNoDataPopover/TimeWindowNoDataPopover";
import { useUserTzContext } from "./hooks/useUserTzContext";
import {
  ALL_TIME_LABEL,
  NO_DATA_POPOVER_HIDE_TIMEOUT_MS,
  NO_DATA_POPOVER_TIMEOUT_MS,
  TIME_WINDOW_ALL_TIME,
} from "./TimeWindow.constants";
import type { TimeWindowProps } from "./TimeWindow.types";
import { urlTimeWindow } from "./TimeWindow.urlSerialization";
import { getTimeWindowFieldLabel } from "./TimeWindow.utils";

const TimeWindowLabel = ({
  timeWindow,
}: {
  timeWindow: TimeWindowProps["timeWindow"];
}) => {
  const { tzStr } = useTimezone();
  const tzCtx = useUserTzContext();

  return timeWindow === TIME_WINDOW_ALL_TIME
    ? ALL_TIME_LABEL
    : getTimeWindowFieldLabel(timeWindow, tzStr, undefined, tzCtx);
};

const TimeWindow = ({ timeWindow, setTimeWindow }: TimeWindowProps) => {
  const selectRef = useRef<HTMLDivElement>(null);
  const [forcedOpen, setForcedOpen] = useState<boolean | undefined>(undefined);
  const [noDataHintOpen, setNoDataHintOpen] = useState(false);
  const [timeWindowNoDataPopoverVisible, setTimeWindowNoDataPopoverVisible] =
    useAtom(timeWindowNoDataPopoverToggleAtom);

  const dateRange = urlTimeWindow.deserializeDateRangeStr(timeWindow);

  const dismissNoDataHint = useCallback(() => {
    setNoDataHintOpen(false);
    setTimeWindowNoDataPopoverVisible(false);
  }, [setTimeWindowNoDataPopoverVisible]);

  const onSelectDifferentTimeWindow = useCallback(() => {
    dismissNoDataHint();
    setForcedOpen(true);
  }, [dismissNoDataHint]);

  const onClose = useCallback(() => {
    setForcedOpen(undefined);
  }, []);

  useEffect(() => {
    if (!timeWindowNoDataPopoverVisible) {
      setNoDataHintOpen(false);
      return;
    }

    const showTimer = setTimeout(() => {
      setNoDataHintOpen(true);
    }, NO_DATA_POPOVER_TIMEOUT_MS);

    return () => {
      clearTimeout(showTimer);
    };
  }, [timeWindowNoDataPopoverVisible]);

  useEffect(() => {
    if (!noDataHintOpen) {
      return;
    }

    const hideTimer = setTimeout(() => {
      setTimeWindowNoDataPopoverVisible(false);
    }, NO_DATA_POPOVER_HIDE_TIMEOUT_MS);

    return () => {
      clearTimeout(hideTimer);
    };
  }, [noDataHintOpen, setTimeWindowNoDataPopoverVisible]);

  useEffect(() => {
    setForcedOpen(false);
  }, [timeWindow]);

  return (
    <>
      <SelectPopover
        ref={selectRef as RefObject<HTMLDivElement>}
        popoverMinAutoFit
        phCaptureAction="filter_time_window"
        data-test-id="app-filters--time-window"
        open={forcedOpen}
        onClose={onClose}
        onClick={dismissNoDataHint}
        size="fit"
        minSize="fit"
        className="whitespace-nowrap"
        label={<TimeWindowLabel timeWindow={timeWindow} />}
        legend="Time Window"
        slotProps={{
          Popover: {
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            transformOrigin: { vertical: "top", horizontal: "right" },
            sx: { margin: 0 },
          },
          OutlinedContainer: {
            startAdornment: <DateTimeIcon color="action" />,
            clearable: false,
            inputProps: {
              "data-test-id": "time-window--select-value",
            },
          },
        }}
      >
        <TimeWindowContent
          timeWindow={timeWindow}
          setTimeWindow={setTimeWindow}
          customRangeValue={dateRange}
        />
      </SelectPopover>

      <TimeWindowNoDataPopover
        open={noDataHintOpen}
        anchorRef={selectRef}
        onSelectDifferentTimeWindow={onSelectDifferentTimeWindow}
      />
    </>
  );
};

export default TimeWindow;
